import React from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Container from '@amzn/awsui-components-console/container';
import Popover from '@amzn/awsui-components-console/popover';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import { TranslationProgress, TranslationStatus } from '../types';
import {
  CANCELLATION_PENDING_LOCK_REASON_KEY,
  SUBMISSION_PENDING_LOCK_REASON_KEY,
  SUBMIT_NO_STRINGS_LOCK_REASON_KEY,
} from '../constants';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface SubmitCancelActionBarProps {
  hasPendingChanges: boolean;
  /**
   * Whether the group currently holds at least one string (server strings that
   * survive pending deletions, plus newly drafted ones). Submit is disabled
   * while this is false: a group is created in TRANSCOM on its first submission,
   * and an empty submission is rejected. Defaults to true so callers that never
   * reach an empty state need not pass it.
   */
  hasStrings?: boolean;
  translationStatus: TranslationStatus;
  translationProgress: TranslationProgress;
  submitLoading: boolean;
  /**
   * When false, Submit and Cancel are disabled: either another client owns the group
   * or the caller lacks SOURCE_MANAGEMENT. Use {@link isEditableClient} to tell which.
   */
  canEdit?: boolean;

  /**
   * The ownership half of `canEdit`, separate because the disabled-Cancel copy names a
   * reason and `canEdit` alone cannot distinguish ownership from a missing permission.
   */
  isEditableClient?: boolean;

  /**
   * Whether the permission answer is still in flight. Both controls show a skeleton until it
   * lands: with a translation in progress, Cancel would otherwise be clickable before the
   * permission is known and the click would 403. Reason popovers are suppressed too, since no
   * reason is established yet.
   */
  permissionsLoading?: boolean;
  /**
   * When true, a submission is awaiting TRANSCOM confirmation. Submit and Cancel
   * are both locked with a visible reason on hover: the group is frozen until
   * the in-flight submission is confirmed, and it can be neither re-submitted
   * nor cancelled meanwhile.
   */
  submissionPending?: boolean;
  /**
   * When true, a cancellation has already been requested for the in-flight job
   * but the group is still reported in progress. Cancel is locked with a visible
   * reason on hover so the user cannot re-issue a cancellation that TRANSCOM is
   * still processing; it clears once the status leaves "in progress". Submit is
   * unaffected — it is already locked by the in-progress status.
   */
  cancellationPending?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  /**
   * Discards all unsaved draft changes. When provided, a "Clear draft" button
   * is shown while there are pending changes so the user can abandon the draft
   * without submitting it.
   */
  onClearDraft?: () => void;
}

const SubmitCancelActionBar: React.FC<SubmitCancelActionBarProps> = ({
  hasPendingChanges,
  hasStrings = true,
  translationStatus,
  translationProgress,
  submitLoading,
  canEdit = true,
  isEditableClient = true,
  permissionsLoading = false,
  submissionPending = false,
  cancellationPending = false,
  onSubmit,
  onCancel,
  onClearDraft,
}) => {
  const { t } = useTranslation();
  const isInProgress = translationStatus === TranslationStatus.InProgress;
  const isCancelled = translationStatus === TranslationStatus.Canceled;

  // Submit disabled when: read-only group OR no pending changes OR the group
  // holds no strings (nothing to send; TRANSCOM rejects an empty submission) OR
  // currently loading OR a translation is already in progress (resubmission is
  // blocked while in flight, matching the "cannot be resubmitted" warning below)
  // OR a prior submission is still awaiting TRANSCOM confirmation.
  // `!canEdit` only counts once the answer is known.
  const permissionDenied = !permissionsLoading && !canEdit;

  const submitDisabled =
    permissionDenied ||
    !hasPendingChanges ||
    !hasStrings ||
    submitLoading ||
    isInProgress ||
    submissionPending;

  // Cancel disabled when: read-only group OR a submission is awaiting
  // confirmation OR a cancellation is already in flight for this job OR
  // translation is not in progress.
  const cancelDisabled =
    permissionDenied ||
    submissionPending ||
    cancellationPending ||
    !isInProgress;

  // Copy for the disabled Cancel, naming the reason that actually applies.
  // `undefined` when the only reason is a missing permission: that gets no
  // explanatory copy, matching the rest of the gated controls.
  const cancelDisabledReason = !isEditableClient
    ? t('sgd-lock-cancel-foreign-client')
    : submissionPending
      ? t(SUBMISSION_PENDING_LOCK_REASON_KEY)
      : cancellationPending
        ? t(CANCELLATION_PENDING_LOCK_REASON_KEY)
        : !isInProgress
          ? t('sgd-lock-cancel-not-in-progress')
          : undefined;

  // The Submit button's hover popover: the lock reason while a submission is
  // pending, then the no-strings reason on an empty group, otherwise the
  // standard explanation of what Submit does.
  const submitPopoverContent = submissionPending
    ? t(SUBMISSION_PENDING_LOCK_REASON_KEY)
    : !hasStrings
      ? t(SUBMIT_NO_STRINGS_LOCK_REASON_KEY)
      : t('sgd-submit-popover-explainer');

  const disabledCancelButton = (
    <Button disabled>
      <Box color="text-status-inactive" fontWeight="bold">
        {t('sgd-cancel-translation')}
      </Box>
    </Button>
  );

  // Cancellation context calculations — include all locales for accurate percentage
  const completedCount = translationProgress.completedLocales.length;
  const totalCount =
    translationProgress.completedLocales.length +
    translationProgress.inProgressLocales.length +
    translationProgress.failedLocales.length +
    translationProgress.cancelledLocales.length;
  const progress =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <Container>
      <SpaceBetween size="m">
        <SpaceBetween direction="horizontal" size="xs">
          {permissionsLoading ? (
            <>
              <Skeleton display="inline-block" height="32px" width="230px" />
              <Skeleton display="inline-block" height="32px" width="150px" />
            </>
          ) : (
            <>
              <Popover
                content={submitPopoverContent}
                dismissButton={false}
                position="top"
                size="medium"
                triggerType="custom"
              >
                <Button
                  variant="primary"
                  disabled={submitDisabled}
                  loading={submitLoading}
                  onClick={onSubmit}
                  iconName="status-info"
                >
                  {t('sgd-submit-group')}
                </Button>
              </Popover>
              {cancelDisabled ? (
                cancelDisabledReason ? (
                  <Popover
                    content={cancelDisabledReason}
                    dismissButton={false}
                    position="top"
                    size="small"
                    triggerType="custom"
                  >
                    {disabledCancelButton}
                  </Popover>
                ) : (
                  disabledCancelButton
                )
              ) : (
                <Button onClick={onCancel}>
                  <Box color="text-status-error" fontWeight="bold">
                    {t('sgd-cancel-translation')}
                  </Box>
                </Button>
              )}
              {/* Discard the local draft. Purely client-side (removes the
                  persisted draft), so it is not permission-gated — but it is
                  hidden without pending changes and locked while a submission
                  is in flight, since the draft is intentionally retained across
                  that window (see StringGroupDetailPage). */}
              {hasPendingChanges && onClearDraft && (
                <Button
                  onClick={onClearDraft}
                  disabled={submitLoading || submissionPending}
                >
                  {t('sgd-clear-draft')}
                </Button>
              )}
            </>
          )}
        </SpaceBetween>

        {/* A requested cancel replaces the "under translation" warning until TRANSCOM confirms it. */}
        {isInProgress &&
          (cancellationPending ? (
            <Alert type="info">{t('sgd-alert-cancellation-requested')}</Alert>
          ) : (
            <Alert type="warning">{t('sgd-alert-under-translation')}</Alert>
          ))}

        {isCancelled && totalCount > 0 && (
          // One resource with named parameters rather than JSX interleaving the
          // three numbers: the clause order around them differs by locale, and a
          // translator cannot reorder fragments spliced together here.
          <Alert type="warning">
            {t('sgd-alert-cancelled-partial', {
              progress,
              completedCount,
              totalCount,
            })}
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default SubmitCancelActionBar;
